import { ethers } from 'ethers';
import type { MetamaskConnectEVM } from '@metamask/connect-evm';

export const WORLD_CHAIN_ID = 480;
const WORLD_CHAIN_HEX_ID = '0x1e0';
const WORLD_CHAIN_RPC_URL = import.meta.env.VITE_WORLD_CHAIN_RPC_URL
  || 'https://worldchain-mainnet.g.alchemy.com/public';
const METAMASK_CONNECT_DAPP_NAME = 'Bubble Wallet';

type WalletProvider = NonNullable<Window['ethereum']>;

let metaMaskConnectClientPromise: Promise<MetamaskConnectEVM> | null = null;

function getDappUrl(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

async function getMetaMaskConnectClient(): Promise<MetamaskConnectEVM> {
  if (!metaMaskConnectClientPromise) {
    metaMaskConnectClientPromise = import('@metamask/connect-evm')
      .then(({ createEVMClient }) => createEVMClient({
        dapp: {
          name: METAMASK_CONNECT_DAPP_NAME,
          url: getDappUrl(),
          iconUrl: `${window.location.origin}/image/bubblewallet_logo_03.png`,
        },
        api: {
          supportedNetworks: {
            [WORLD_CHAIN_HEX_ID]: WORLD_CHAIN_RPC_URL,
          },
        },
        ui: {
          preferExtension: true,
          // Keep mobile deeplinks enabled instead of showing an extension install modal.
          showInstallModal: false,
        },
        mobile: {
          useDeeplink: true,
          preferredOpenLink: (deeplink: string) => {
            window.location.assign(deeplink);
          },
        },
        analytics: { enabled: false },
      }));

    void metaMaskConnectClientPromise.catch(() => {
      metaMaskConnectClientPromise = null;
    });
  }

  return metaMaskConnectClientPromise;
}

function getInjectedProvider(): NonNullable<Window['ethereum']> {
  if (!window.ethereum) {
    throw new Error('找不到 MetaMask 連接器，請按連接按鈕開啟 MetaMask App。');
  }
  return window.ethereum;
}

export async function getMetaMaskProvider(): Promise<WalletProvider> {
  if (window.ethereum) return window.ethereum;

  const client = await getMetaMaskConnectClient();
  return client.getProvider() as unknown as WalletProvider;
}

function getProviderErrorCode(error: unknown): number | null {
  if (typeof error !== 'object' || error === null || !('code' in error)) return null;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'number' ? code : null;
}

export async function getConnectedMetaMaskAddress(): Promise<string | null> {
  const provider = await getMetaMaskProvider();
  const accounts = await provider.request({ method: 'eth_accounts' });
  if (!Array.isArray(accounts) || typeof accounts[0] !== 'string' || !ethers.isAddress(accounts[0])) {
    return null;
  }
  return ethers.getAddress(accounts[0]);
}

export async function ensureMetaMaskWorldChain(provider?: WalletProvider): Promise<void> {
  const activeProvider = provider ?? await getMetaMaskProvider();
  const currentChainId = await activeProvider.request({ method: 'eth_chainId' });
  if (String(currentChainId).toLowerCase() === WORLD_CHAIN_HEX_ID) return;

  try {
    await activeProvider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: WORLD_CHAIN_HEX_ID }],
    });
  } catch (error) {
    if (getProviderErrorCode(error) !== 4902) {
      throw new Error('MetaMask 尚未切換到 World Chain，交易已取消。');
    }
    await activeProvider.request({
      method: 'wallet_addEthereumChain',
      params: [{
        chainId: WORLD_CHAIN_HEX_ID,
        chainName: 'World Chain',
        nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
        rpcUrls: [WORLD_CHAIN_RPC_URL],
        blockExplorerUrls: ['https://worldscan.org'],
      }],
    });
  }
}

export async function connectMetaMask(): Promise<string> {
  if (window.ethereum) {
    const provider = getInjectedProvider();
    const accounts = await provider.request({ method: 'eth_requestAccounts' });
    if (!Array.isArray(accounts) || typeof accounts[0] !== 'string' || !ethers.isAddress(accounts[0])) {
      throw new Error('MetaMask 沒有回傳有效的錢包地址。');
    }
    await ensureMetaMaskWorldChain(provider);
    return ethers.getAddress(accounts[0]);
  }

  const client = await getMetaMaskConnectClient();
  const { accounts } = await client.connect({ chainIds: [WORLD_CHAIN_HEX_ID] });
  if (!accounts[0] || !ethers.isAddress(accounts[0])) {
    throw new Error('MetaMask 沒有回傳有效的錢包地址。');
  }
  await ensureMetaMaskWorldChain(client.getProvider() as unknown as WalletProvider);
  return ethers.getAddress(accounts[0]);
}

export async function disconnectMetaMask(): Promise<void> {
  if (window.ethereum) {
    try {
      await window.ethereum.request({
        method: 'wallet_revokePermissions',
        params: [{ eth_accounts: {} }],
      });
    } catch (error) {
      const errorCode = getProviderErrorCode(error);
      if (errorCode === 4001) {
        throw new Error('你已取消中斷 MetaMask 連接。');
      }
      if (errorCode !== 4200 && errorCode !== -32601) {
        throw new Error('無法中斷 MetaMask 連接，請在 MetaMask 內手動移除網站連接。');
      }
      // Some injected providers do not implement wallet_revokePermissions.
      // The app still clears its local active-wallet state below.
    }
    return;
  }

  if (!metaMaskConnectClientPromise) return;

  const client = await getMetaMaskConnectClient();
  await client.disconnect();
  metaMaskConnectClientPromise = null;
}

export async function getMetaMaskSigner(): Promise<{
  address: string;
  provider: ethers.BrowserProvider;
  signer: ethers.JsonRpcSigner;
}> {
  const walletProvider = await getMetaMaskProvider();
  await ensureMetaMaskWorldChain(walletProvider);
  const provider = new ethers.BrowserProvider(walletProvider);
  const signer = await provider.getSigner();
  return { address: await signer.getAddress(), provider, signer };
}
